import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useConfirm } from '../contexts/ConfirmContext';

// ============ TYPES ============

interface CourierContract {
  id: string;
  originLocationId: string;
  originLocationName: string;
  originX: number;
  originY: number;
  destinationLocationId: string;
  destinationLocationName: string;
  destinationX: number;
  destinationY: number;
  contractType: 'standard' | 'regulated';
  packageName: string;
  packageDescription: string;
  packageRarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  rewardPieces: number;
  rewardXp: number;
  rewardReputation: number;
  timeLimitMinutes: number | null;
  expiresAt: number | null;
  status: string;
}

interface CourierPackage {
  id: string;
  contractId: string;
  itemId: string;
  name: string;
  description: string;
  rarity: string;
  destinationLocationId: string;
  destinationLocationName: string;
  destinationX: number;
  destinationY: number;
  rewardPieces: number;
  timeLimitExpiresAt: number | null;
  status: string;
}

interface CourierStatistics {
  totalDeliveries: number;
  successfulDeliveries: number;
  failedDeliveries: number;
  stolenPackages: number;
  packagesLostToPvp: number;
  totalPiecesEarned: number;
  currentStreak: number;
  bestStreak: number;
}

interface CourierBoardInlineProps {
  socket: Socket | null;
  locationId: string;
  locationName: string;
  locationType: 'trading_post' | 'harbor_office';
  onSetDestination?: (x: number, y: number, name: string) => void;
}

// ============ RARITY COLORS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

// ============ COMPONENT ============

export const CourierBoardInline: React.FC<CourierBoardInlineProps> = ({
  socket,
  locationId,
  locationName,
  locationType,
  onSetDestination,
}) => {
  const { confirm } = useConfirm();
  const [contracts, setContracts] = useState<CourierContract[]>([]);
  const [activeContracts, setActiveContracts] = useState<CourierContract[]>([]);
  const [activePackages, setActivePackages] = useState<CourierPackage[]>([]);
  const [stats, setStats] = useState<CourierStatistics | null>(null);
  const [activeTab, setActiveTab] = useState<'available' | 'active' | 'stats'>('available');
  const [selectedContract, setSelectedContract] = useState<CourierContract | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Request data on mount
  useEffect(() => {
    if (socket && locationId) {
      setIsLoading(true);
      socket.emit('courier:getContracts', { locationId });
      socket.emit('courier:getActiveContracts');
      socket.emit('courier:getStats');
    }
  }, [socket, locationId]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleContracts = (data: { locationId: string; contracts: CourierContract[] }): void => {
      if (data.locationId === locationId) {
        setContracts(data.contracts);
        setIsLoading(false);
      }
    };

    const handleActiveContracts = (data: { contracts: CourierContract[]; packages: CourierPackage[] }): void => {
      setActiveContracts(data.contracts);
      setActivePackages(data.packages);
    };

    const handleStats = (data: { stats: CourierStatistics | null }): void => {
      setStats(data.stats);
    };

    const handleAccepted = (data: { contract: CourierContract; message: string }): void => {
      setMessage(data.message);
      setSelectedContract(null);
      socket.emit('courier:getContracts', { locationId });
      socket.emit('courier:getActiveContracts');
      setTimeout(() => setMessage(''), 3000);
    };

    const handleDelivered = (data: { message: string; reward: { pieces: number } }): void => {
      setMessage(`${data.message}`);
      socket.emit('courier:getActiveContracts');
      socket.emit('courier:getStats');
      setTimeout(() => setMessage(''), 5000);
    };

    const handleAbandoned = (data: { message: string }): void => {
      setMessage(data.message);
      socket.emit('courier:getActiveContracts');
      setTimeout(() => setMessage(''), 3000);
    };

    const handleError = (data: { message: string }): void => {
      setMessage(`Error: ${data.message}`);
      setTimeout(() => setMessage(''), 3000);
    };

    socket.on('courier:contracts', handleContracts);
    socket.on('courier:activeContracts', handleActiveContracts);
    socket.on('courier:stats', handleStats);
    socket.on('courier:accepted', handleAccepted);
    socket.on('courier:delivered', handleDelivered);
    socket.on('courier:abandoned', handleAbandoned);
    socket.on('courier:error', handleError);

    return () => {
      socket.off('courier:contracts', handleContracts);
      socket.off('courier:activeContracts', handleActiveContracts);
      socket.off('courier:stats', handleStats);
      socket.off('courier:accepted', handleAccepted);
      socket.off('courier:delivered', handleDelivered);
      socket.off('courier:abandoned', handleAbandoned);
      socket.off('courier:error', handleError);
    };
  }, [socket, locationId]);

  const handleAcceptContract = useCallback((contractId: string): void => {
    socket?.emit('courier:accept', { contractId });
  }, [socket]);

  const handleDeliverPackage = useCallback((packageId: string): void => {
    socket?.emit('courier:deliver', { packageId, locationId });
  }, [socket, locationId]);

  const handleAbandonContract = useCallback((contractId: string): void => {
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Abandon Contract',
        message: 'Are you sure you want to abandon this contract? You will lose the package.',
        confirmText: 'Abandon',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket?.emit('courier:abandon', { contractId });
    })();
  }, [socket, confirm]);

  const handleSetDestination = useCallback((x: number, y: number, name: string): void => {
    if (onSetDestination) {
      onSetDestination(x, y, name);
    }
  }, [onSetDestination]);

  const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.round(Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)) / 16); // Divide by TILE_SIZE=16
  };

  const formatTimeLimit = (minutes: number | null): string => {
    if (!minutes) return 'No limit';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTimeRemaining = (expiresAt: number | null): string => {
    if (!expiresAt) return 'No limit';
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return 'EXPIRED';
    const minutes = Math.floor(remaining / 60000);
    if (minutes < 60) return `${minutes}m remaining`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m remaining`;
  };

  const getDeliverablePackage = (): CourierPackage | null => {
    return activePackages.find(pkg => pkg.destinationLocationId === locationId) || null;
  };

  const deliverablePackage = getDeliverablePackage();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>

      {/* Deliverable Package Alert */}
      {deliverablePackage && (
        <div
          style={{
            backgroundColor: '#2a4a2a',
            border: '2px solid #4a8a4a',
            margin: '0 12px 12px 12px',
            padding: '10px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ color: '#8f8', fontWeight: 'bold', fontSize: '12px' }}>
              DELIVERY READY
            </div>
            <div style={{ color: '#cfc', fontSize: '11px' }}>
              {deliverablePackage.name} — {deliverablePackage.rewardPieces.toLocaleString()}₱
            </div>
          </div>
          <button
            onClick={() => handleDeliverPackage(deliverablePackage.id)}
            style={{
              backgroundColor: '#4a8a4a',
              border: 'none',
              color: '#fff',
              padding: '6px 12px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '11px',
            }}
          >
            DELIVER
          </button>
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #30363d', margin: '0 12px' }}>
        {(['available', 'active', 'stats'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: '10px 8px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #f59e0b' : '2px solid transparent',
              color: activeTab === tab ? '#f59e0b' : '#8b949e',
              cursor: 'pointer',
              textTransform: 'uppercase',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            {tab === 'available' && `Available (${contracts.length})`}
            {tab === 'active' && `Active (${activeContracts.length})`}
            {tab === 'stats' && 'Stats'}
          </button>
        ))}
      </div>

      {/* Message */}
      {message && (
        <div
          style={{
            padding: '8px 12px',
            margin: '0 12px 12px 12px',
            backgroundColor: message.startsWith('Error') ? 'rgba(239, 68, 68, 0.10)' : 'rgba(34, 197, 94, 0.10)',
            color: message.startsWith('Error') ? '#ef4444' : '#22c55e',
            fontSize: '11px',
            textAlign: 'center',
            border: '1px solid ' + (message.startsWith('Error') ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'),
          }}
        >
          {message}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 12px 0 12px' }}>
        {isLoading ? (
          <div style={{ color: '#888', textAlign: 'center', padding: '40px', fontSize: '12px' }}>
            Loading contracts...
          </div>
        ) : activeTab === 'available' ? (
          contracts.length === 0 ? (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px', fontSize: '12px' }}>
              No contracts available at this location.
              <br />
              <span style={{ fontSize: '10px' }}>Check back later or try another location.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {contracts.map((contract) => (
                <div
                  key={contract.id}
                  style={{
                    backgroundColor: selectedContract?.id === contract.id ? '#161b22' : '#0d1117',
                    border: `1px solid ${selectedContract?.id === contract.id ? RARITY_COLORS[contract.packageRarity] : '#30363d'}`,
                    padding: '12px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedContract(contract)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: RARITY_COLORS[contract.packageRarity], fontWeight: 'bold', fontSize: '12px' }}>
                        {contract.packageName}
                      </div>
                      <div style={{ color: '#8b949e', fontSize: '10px', marginTop: '2px' }}>
                        {RARITY_LABELS[contract.packageRarity]} {contract.contractType === 'regulated' ? 'Regulated' : 'Standard'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '12px' }}>
                        {contract.rewardPieces.toLocaleString()}₱
                      </div>
                      {contract.rewardXp > 0 && (
                        <div style={{ color: '#8af', fontSize: '10px' }}>
                          +{contract.rewardXp} XP
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ color: '#c9d1d9', fontSize: '10px', marginTop: '8px', lineHeight: '1.4' }}>
                    {contract.packageDescription}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '8px', fontSize: '10px' }}>
                    <div style={{ color: '#8b949e' }}>
                      <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Destination</div>
                      <span style={{ color: '#aaf' }}>{contract.destinationLocationName}</span>
                    </div>
                    <div style={{ color: '#8b949e' }}>
                      <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Distance</div>
                      {calculateDistance(contract.originX, contract.originY, contract.destinationX, contract.destinationY)} t
                    </div>
                    <div style={{ color: contract.timeLimitMinutes ? '#fa8' : '#8b949e' }}>
                      <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Time Limit</div>
                      {formatTimeLimit(contract.timeLimitMinutes)}
                    </div>
                  </div>

                  {selectedContract?.id === contract.id && (
                    <div style={{ marginTop: '10px', display: 'flex', gap: '6px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptContract(contract.id);
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: '#238636',
                          border: '1px solid #2ea043',
                          color: '#ffffff',
                          padding: '6px',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: '10px',
                        }}
                      >
                        ACCEPT
                      </button>
                      {onSetDestination && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDestination(contract.destinationX, contract.destinationY, contract.destinationLocationName);
                          }}
                          style={{
                            backgroundColor: '#21262d',
                            border: '1px solid #30363d',
                            color: '#e6edf3',
                            padding: '6px 10px',
                            cursor: 'pointer',
                            fontSize: '10px',
                          }}
                        >
                          SET DEST
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : activeTab === 'active' ? (
          activeContracts.length === 0 ? (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px', fontSize: '12px' }}>
              No active contracts.
              <br />
              <span style={{ fontSize: '10px' }}>Accept a contract from the Available tab.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {activeContracts.map((contract) => {
                const pkg = activePackages.find(p => p.contractId === contract.id);
                const isDeliverable = pkg?.destinationLocationId === locationId;
                
                return (
                  <div
                    key={contract.id}
                    style={{
                      backgroundColor: isDeliverable ? 'rgba(34, 197, 94, 0.10)' : '#0d1117',
                      border: `1px solid ${isDeliverable ? '#2ea043' : '#30363d'}`,
                      padding: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: RARITY_COLORS[contract.packageRarity], fontWeight: 'bold', fontSize: '12px' }}>
                          {contract.packageName}
                        </div>
                        <div style={{ color: '#8b949e', fontSize: '10px', marginTop: '2px' }}>
                          {pkg ? 'In Transit' : 'Accepted'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '12px' }}>
                          {contract.rewardPieces.toLocaleString()}₱
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px', fontSize: '10px' }}>
                      <div style={{ color: '#8b949e' }}>
                        <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Destination</div>
                        <span style={{ color: '#aaf' }}>{contract.destinationLocationName}</span>
                      </div>
                      {pkg?.timeLimitExpiresAt && (
                        <div style={{ color: pkg.timeLimitExpiresAt - Date.now() < 300000 ? '#f88' : '#fa8' }}>
                          <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Time Remaining</div>
                          {getTimeRemaining(pkg.timeLimitExpiresAt)}
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: '10px', display: 'flex', gap: '6px' }}>
                      {isDeliverable && pkg && (
                        <button
                          onClick={() => handleDeliverPackage(pkg.id)}
                          style={{
                            flex: 1,
                            backgroundColor: '#238636',
                            border: '1px solid #2ea043',
                            color: '#ffffff',
                            padding: '6px',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '10px',
                          }}
                        >
                          DELIVER NOW
                        </button>
                      )}
                      {onSetDestination && !isDeliverable && (
                        <button
                          onClick={() => handleSetDestination(contract.destinationX, contract.destinationY, contract.destinationLocationName)}
                          style={{
                            flex: 1,
                            backgroundColor: '#21262d',
                            border: '1px solid #30363d',
                            color: '#e6edf3',
                            padding: '6px',
                            cursor: 'pointer',
                            fontSize: '10px',
                          }}
                        >
                          SET DESTINATION
                        </button>
                      )}
                      <button
                        onClick={() => handleAbandonContract(contract.id)}
                        style={{
                          backgroundColor: '#21262d',
                          border: '1px solid rgba(239, 68, 68, 0.45)',
                          color: '#ef4444',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontSize: '10px',
                        }}
                      >
                        ABANDON
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          // Stats tab
          <div>
            {stats ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div style={{ backgroundColor: '#0d1117', padding: '12px', border: '1px solid #30363d' }}>
                  <div style={{ color: '#8b949e', fontSize: '9px', textTransform: 'uppercase', marginBottom: '6px' }}>Total Deliveries</div>
                  <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{stats.totalDeliveries}</div>
                </div>
                <div style={{ backgroundColor: '#0d1117', padding: '12px', border: '1px solid #30363d' }}>
                  <div style={{ color: '#8b949e', fontSize: '9px', textTransform: 'uppercase', marginBottom: '6px' }}>Success Rate</div>
                  <div style={{ color: '#8f8', fontSize: '18px', fontWeight: 'bold' }}>
                    {stats.totalDeliveries > 0 
                      ? Math.round((stats.successfulDeliveries / stats.totalDeliveries) * 100) 
                      : 0}%
                  </div>
                </div>
                <div style={{ backgroundColor: '#0d1117', padding: '12px', border: '1px solid #30363d' }}>
                  <div style={{ color: '#8b949e', fontSize: '9px', textTransform: 'uppercase', marginBottom: '6px' }}>Total Earned</div>
                  <div style={{ color: '#ffd700', fontSize: '18px', fontWeight: 'bold' }}>
                    {stats.totalPiecesEarned.toLocaleString()}₱
                  </div>
                </div>
                <div style={{ backgroundColor: '#0d1117', padding: '12px', border: '1px solid #30363d' }}>
                  <div style={{ color: '#8b949e', fontSize: '9px', textTransform: 'uppercase', marginBottom: '6px' }}>Current Streak</div>
                  <div style={{ color: '#8af', fontSize: '18px', fontWeight: 'bold' }}>{stats.currentStreak}</div>
                  <div style={{ color: '#666', fontSize: '9px', marginTop: '4px' }}>Best: {stats.bestStreak}</div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#888', textAlign: 'center', padding: '40px', fontSize: '12px' }}>
                No courier statistics yet.
                <br />
                <span style={{ fontSize: '10px' }}>Complete your first delivery to start tracking!</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourierBoardInline;
